interface BrowserInfo {
  os: string;
  brand: string;
  device: DeviceType;
  method: DetectionMethod;
}

interface BrowserTest {
  name: string;
  regex: RegExp;
  exclude?: RegExp;
}

interface UserAgentData {
  brands: Array<{ brand: string; version: string }>;
  platform: string;
  mobile: boolean;
}

interface HighEntropyValues {
  platform?: string;
  platformVersion?: string;
  model?: string;
  uaFullVersion?: string;
  brands?: Array<{ brand: string; version: string }>;
  fullVersionList?: Array<{ brand: string; version: string }>;
}

type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'tv' | 'bot';
type DetectionMethod = 'userAgentData' | 'comprehensive-parsing';

// Extend the Navigator interface to include userAgentData
//   declare global {
//     interface Navigator {
//       userAgentData?: {
//         brands: Array<{ brand: string; version: string }>;
//         platform: string;
//         mobile: boolean;
//         getHighEntropyValues(hints: string[]): Promise<HighEntropyValues>;
//       };
//     }
//   }

// Comprehensive browser detection covering all possible browsers
export const getAllBrowserInfo = async (): Promise<BrowserInfo> => {
  const isMobileApp = navigator.userAgent.indexOf('gonative') > -1;

  // Try User-Agent Client Hints API first (modern Chromium browsers)
  if ('userAgentData' in navigator && navigator.userAgentData) {
    try {
      const uaData = navigator.userAgentData;
      const highEntropyValues = await uaData.getHighEntropyValues([
        'platform',
        'platformVersion',
        'model',
        'uaFullVersion',
        'brands',
        'fullVersionList',
      ]);

      const brands = highEntropyValues.fullVersionList || uaData.brands;

      // Enhanced exclusion list for fake brands
      const fakeBrands = [
        'Chromium',
        'Not_A Brand',
        'Not A;Brand',
        'Not/A)Brand',
        '(Not(A:Brand',
        'Not)A;Brand',
        'Not;A=Brand',
        'Not A Brand',
        ' Not A;Brand',
        'Not-A.Brand',
        'NotABrand',
      ];

      // Find the real browser brand by excluding fake ones
      const primaryBrand = brands?.find(
        (b: { brand: string; version: string }) =>
          !fakeBrands.some(
            (fakeBrand) => b.brand.includes(fakeBrand) || fakeBrand.includes(b.brand),
          ),
      );

      // If we found a real brand, use it; otherwise fall back to comprehensive parsing
      if (primaryBrand && primaryBrand.brand !== 'Chromium') {
        return {
          os: detectOS(navigator.userAgent, highEntropyValues.platform || uaData.platform),
          brand: isMobileApp
            ? 'Mobile App - GoNative'
            : `${primaryBrand.brand}${primaryBrand.version ? ` V${primaryBrand.version}` : ''}`,
          device: detectDeviceType(navigator.userAgent),
          method: 'userAgentData',
        };
      }

      // If no real brand found, fall back to parsing
      return parseAllUserAgents(navigator.userAgent);
    } catch (error) {
      // console.warn('Failed to get high entropy values:', error);
    }
  }

  // Fallback to comprehensive User-Agent string parsing
  return parseAllUserAgents(navigator.userAgent);
};

const detectOS = (userAgent: string, platform?: string, platformVersion?: string): string => {
  const ua = userAgent.toLowerCase();

  // Windows versions (most specific first)
  if (ua.includes('windows nt 10.0') && ua.includes('22000')) return 'Windows 11';
  if (ua.includes('windows nt 10.0')) return 'Windows 10';
  if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
  if (ua.includes('windows nt 6.2')) return 'Windows 8';
  if (ua.includes('windows nt 6.1')) return 'Windows 7';
  if (ua.includes('windows nt 6.0')) return 'Windows Vista';
  if (ua.includes('windows nt 5.2')) return 'Windows Server 2003';
  if (ua.includes('windows nt 5.1')) return 'Windows XP';
  if (ua.includes('windows nt 5.0')) return 'Windows 2000';
  if (ua.includes('windows 98')) return 'Windows 98';
  if (ua.includes('windows 95')) return 'Windows 95';
  if (ua.includes('win16')) return 'Windows 3.1';

  // macOS versions - prioritize platformVersion from User-Agent Client Hints
  if (platform?.toLowerCase() === 'macos' && platformVersion) {
    const version = platformVersion;
    const versionNum = parseFloat(version);

    if (versionNum >= 15.1) return `macOS Sequoia ${version}`;
    if (versionNum >= 15.0) return `macOS Sequoia ${version}`;
    if (versionNum >= 14.0) return `macOS Sonoma ${version}`;
    if (versionNum >= 13.0) return `macOS Ventura ${version}`;
    if (versionNum >= 12.0) return `macOS Monterey ${version}`;
    if (versionNum >= 11.0) return `macOS Big Sur ${version}`;
    if (versionNum >= 10.15) return `macOS Catalina ${version}`;
    if (versionNum >= 10.14) return `macOS Mojave ${version}`;

    return `macOS ${version}`;
  }

  // Fallback to User-Agent parsing for macOS (will be frozen at 10.15.7 in modern browsers)
  const macMatch = ua.match(/mac os x ([0-9._]+)/);
  if (macMatch) {
    const version = macMatch[1].replace(/_/g, '.');
    const versionNum = parseFloat(version);

    // If we detect the frozen version, indicate it might be inaccurate
    if (version === '10.15.7') {
      return 'macOS (version frozen at 10.15.7 for privacy)';
    }

    if (versionNum >= 15.0) return `macOS Sequoia ${version}`;
    if (versionNum >= 14.0) return `macOS Sonoma ${version}`;
    if (versionNum >= 13.0) return `macOS Ventura ${version}`;
    if (versionNum >= 12.0) return `macOS Monterey ${version}`;
    if (versionNum >= 11.0) return `macOS Big Sur ${version}`;
    if (versionNum >= 10.15) return `macOS Catalina ${version}`;
    if (versionNum >= 10.14) return `macOS Mojave ${version}`;

    return `macOS ${version}`;
  }

  // iOS versions
  const iosMatch = ua.match(/os ([0-9._]+) like mac os x/);
  if (iosMatch) return `iOS ${iosMatch[1].replace(/_/g, '.')}`;

  // Android versions
  const androidMatch = ua.match(/android ([0-9.]+)/);
  if (androidMatch) return `Android ${androidMatch[1]}`;

  // Linux distributions
  if (ua.includes('ubuntu')) return 'Ubuntu Linux';
  if (ua.includes('debian')) return 'Debian Linux';
  if (ua.includes('fedora')) return 'Fedora Linux';
  if (ua.includes('suse')) return 'SUSE Linux';
  if (ua.includes('red hat')) return 'Red Hat Linux';
  if (ua.includes('centos')) return 'CentOS Linux';
  if (ua.includes('mint')) return 'Linux Mint';
  if (ua.includes('arch')) return 'Arch Linux';
  if (ua.includes('manjaro')) return 'Manjaro Linux';
  if (ua.includes('elementary')) return 'elementary OS';
  if (ua.includes('zorin')) return 'Zorin OS';
  if (ua.includes('linux')) return 'Linux';

  // Other operating systems
  if (ua.includes('freebsd')) return 'FreeBSD';
  if (ua.includes('openbsd')) return 'OpenBSD';
  if (ua.includes('netbsd')) return 'NetBSD';
  if (ua.includes('dragonfly')) return 'DragonFly BSD';
  if (ua.includes('sunos')) return 'SunOS';
  if (ua.includes('aix')) return 'AIX';
  if (ua.includes('hpux')) return 'HP-UX';
  if (ua.includes('irix')) return 'IRIX';
  if (ua.includes('osf1')) return 'OSF1';
  if (ua.includes('qnx')) return 'QNX';
  if (ua.includes('beos')) return 'BeOS';
  if (ua.includes('haiku')) return 'Haiku';
  if (ua.includes('os/2')) return 'OS/2';
  if (ua.includes('amiga')) return 'AmigaOS';
  if (ua.includes('morphos')) return 'MorphOS';
  if (ua.includes('plan9')) return 'Plan 9';
  if (ua.includes('riscos')) return 'RISC OS';

  // Fallback to platform if available
  return platform || 'Unknown';
};

// Fixed: Removed unnecessary 'else' statements after returns
const detectDeviceType = (userAgent: string): DeviceType => {
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile';
  }

  if (/tablet|ipad/i.test(userAgent)) {
    return 'tablet';
  }

  if (/smart-tv|smarttv|googletv|appletv|hbbtv|pov_tv|netcast/i.test(userAgent)) {
    return 'tv';
  }

  if (/bot|crawler|spider|scraper/i.test(userAgent)) {
    return 'bot';
  }

  return 'desktop';
};

const parseAllUserAgents = (userAgent: string): BrowserInfo => {
  const isMobileApp = navigator.userAgent.indexOf('gonative') > -1;

  const ua = userAgent.toLowerCase();
  let browser = 'Unknown';
  let version = '';

  // Comprehensive browser detection (order matters - most specific first)
  const browserTests: BrowserTest[] = [
    // Microsoft browsers
    { name: 'Microsoft Edge', regex: /edg\/([0-9.]+)/i },
    { name: 'Microsoft Edge Legacy', regex: /edge\/([0-9.]+)/i },
    { name: 'Internet Explorer', regex: /msie ([0-9.]+)/i },
    { name: 'Internet Explorer', regex: /trident.*rv:([0-9.]+)/i },

    // Chromium-based browsers (most specific first)
    { name: 'Arc Browser', regex: /arc\/([0-9.]+)/i },
    { name: 'Brave Browser', regex: /brave\/([0-9.]+)/i },
    { name: 'Vivaldi', regex: /vivaldi\/([0-9.]+)/i },
    { name: 'Opera GX', regex: /opgx\/([0-9.]+)/i },
    { name: 'Opera', regex: /opr\/([0-9.]+)/i },
    { name: 'Opera', regex: /opera\/([0-9.]+)/i },
    { name: 'Yandex Browser', regex: /yabrowser\/([0-9.]+)/i },
    { name: 'UC Browser', regex: /ucbrowser\/([0-9.]+)/i },
    { name: 'Samsung Internet', regex: /samsungbrowser\/([0-9.]+)/i },
    { name: 'MIUI Browser', regex: /miuibrowser\/([0-9.]+)/i },
    { name: 'Xiaomi Browser', regex: /mibrowser\/([0-9.]+)/i },
    { name: 'QQ Browser', regex: /qqbrowser\/([0-9.]+)/i },
    { name: 'Sogou Explorer', regex: /se ([0-9.]+)/i },
    { name: '360 Secure Browser', regex: /360se/i },
    { name: '360 Extreme Browser', regex: /360ee/i },
    { name: 'Maxthon', regex: /maxthon\/([0-9.]+)/i },
    { name: 'Comodo Dragon', regex: /comodo_dragon\/([0-9.]+)/i },
    { name: 'SRWare Iron', regex: /iron\/([0-9.]+)/i },
    { name: 'Cent Browser', regex: /centbrowser\/([0-9.]+)/i },
    { name: 'Coc Coc', regex: /coc_coc_browser\/([0-9.]+)/i },
    { name: 'Torch', regex: /torch\/([0-9.]+)/i },
    { name: 'Chromium', regex: /chromium\/([0-9.]+)/i },
    {
      name: 'Google Chrome',
      regex: /chrome\/([0-9.]+)/i,
      exclude: /edg|opr|opera|brave|vivaldi|yabrowser|ucbrowser|samsung|miui|arc/i,
    },

    // Firefox-based browsers
    { name: 'Mozilla Firefox', regex: /firefox\/([0-9.]+)/i },
    { name: 'Firefox Focus', regex: /focus\/([0-9.]+)/i },
    { name: 'Firefox Reality', regex: /firefoxreality\/([0-9.]+)/i },
    { name: 'Firefox Preview', regex: /fenix\/([0-9.]+)/i },
    { name: 'Waterfox', regex: /waterfox\/([0-9.]+)/i },
    { name: 'Pale Moon', regex: /palemoon\/([0-9.]+)/i },
    { name: 'Basilisk', regex: /basilisk\/([0-9.]+)/i },
    { name: 'SeaMonkey', regex: /seamonkey\/([0-9.]+)/i },
    { name: 'K-Meleon', regex: /k-meleon\/([0-9.]+)/i },
    { name: 'Iceweasel', regex: /iceweasel\/([0-9.]+)/i },
    { name: 'IceCat', regex: /icecat\/([0-9.]+)/i },
    { name: 'GNU IceCat', regex: /icecat\/([0-9.]+)/i },
    { name: 'Cyberfox', regex: /cyberfox\/([0-9.]+)/i },
    { name: 'LibreWolf', regex: /librewolf\/([0-9.]+)/i },

    // Safari and WebKit browsers
    { name: 'Safari', regex: /version\/([0-9.]+).*safari/i, exclude: /chrome|chromium|edg/i },
    { name: 'Mobile Safari', regex: /mobile.*safari/i },
    { name: 'Epiphany', regex: /epiphany\/([0-9.]+)/i },
    { name: 'Midori', regex: /midori\/([0-9.]+)/i },
    { name: 'OmniWeb', regex: /omniweb\/([0-9.]+)/i },
    { name: 'Shiira', regex: /shiira\/([0-9.]+)/i },
    { name: 'Orion', regex: /orion\/([0-9.]+)/i },

    // Mobile browsers
    { name: 'Chrome Mobile', regex: /chrome\/([0-9.]+).*mobile/i },
    { name: 'Firefox Mobile', regex: /mobile.*firefox\/([0-9.]+)/i },
    { name: 'Opera Mobile', regex: /opera.*mobi.*version\/([0-9.]+)/i },
    { name: 'Opera Mini', regex: /opera mini\/([0-9.]+)/i },
    { name: 'Android Browser', regex: /android.*version\/([0-9.]+)/i },
    { name: 'Dolphin Browser', regex: /dolphin\/([0-9.]+)/i },
    { name: 'Puffin Browser', regex: /puffin\/([0-9.]+)/i },
    { name: 'Mercury Browser', regex: /mercury\/([0-9.]+)/i },
    { name: 'Silk Browser', regex: /silk\/([0-9.]+)/i },
    { name: 'BlackBerry Browser', regex: /blackberry.*version\/([0-9.]+)/i },
    { name: 'WebOS Browser', regex: /webos\/([0-9.]+)/i },
    { name: 'Tizen Browser', regex: /tizen.*version\/([0-9.]+)/i },
    { name: 'KaiOS Browser', regex: /kaios\/([0-9.]+)/i },

    // Gaming console browsers
    { name: 'PlayStation Browser', regex: /playstation.*version\/([0-9.]+)/i },
    { name: 'Xbox Browser', regex: /xbox.*version\/([0-9.]+)/i },
    { name: 'Nintendo Browser', regex: /nintendo.*version\/([0-9.]+)/i },
    { name: 'Steam Browser', regex: /valve steam.*version\/([0-9.]+)/i },

    // Desktop application browsers
    { name: 'Electron', regex: /electron\/([0-9.]+)/i },
    { name: 'Nwjs', regex: /nwjs\/([0-9.]+)/i },
    { name: 'PhantomJS', regex: /phantomjs\/([0-9.]+)/i },
    { name: 'SlimerJS', regex: /slimerjs\/([0-9.]+)/i },
    { name: 'Puppeteer', regex: /headlesschrome\/([0-9.]+)/i },
    { name: 'Playwright', regex: /playwright\/([0-9.]+)/i },

    // Legacy browsers
    { name: 'Netscape', regex: /netscape\/([0-9.]+)/i },
    {
      name: 'Mozilla',
      regex: /mozilla\/([0-9.]+)/i,
      exclude: /compatible|webkit|khtml|gecko|trident|edge/i,
    },
    { name: 'Konqueror', regex: /konqueror\/([0-9.]+)/i },
    { name: 'Lynx', regex: /lynx\/([0-9.]+)/i },
    { name: 'Links', regex: /links \(([0-9.]+)/i },
    { name: 'ELinks', regex: /elinks\/([0-9.]+)/i },
    { name: 'w3m', regex: /w3m\/([0-9.]+)/i },

    // Bots and crawlers
    { name: 'Googlebot', regex: /googlebot\/([0-9.]+)/i },
    { name: 'Bingbot', regex: /bingbot\/([0-9.]+)/i },
    { name: 'Slurp', regex: /slurp\/([0-9.]+)/i },
    { name: 'DuckDuckBot', regex: /duckduckbot\/([0-9.]+)/i },
    { name: 'Baiduspider', regex: /baiduspider\/([0-9.]+)/i },
    { name: 'YandexBot', regex: /yandexbot\/([0-9.]+)/i },
    { name: 'facebookexternalhit', regex: /facebookexternalhit\/([0-9.]+)/i },
    { name: 'Twitterbot', regex: /twitterbot\/([0-9.]+)/i },
    { name: 'LinkedInBot', regex: /linkedinbot\/([0-9.]+)/i },
    { name: 'WhatsApp', regex: /whatsapp\/([0-9.]+)/i },
    { name: 'TelegramBot', regex: /telegrambot\/([0-9.]+)/i },
    { name: 'Applebot', regex: /applebot\/([0-9.]+)/i },
    { name: 'Amazonbot', regex: /amazonbot\/([0-9.]+)/i },

    // Specialized browsers
    { name: 'Tor Browser', regex: /firefox.*tor/i },
    { name: 'DuckDuckGo Browser', regex: /ddg_android\/([0-9.]+)/i },
    { name: 'Focus', regex: /focus\/([0-9.]+)/i },
    { name: 'Klar', regex: /klar\/([0-9.]+)/i },
    { name: 'Coast', regex: /coast\/([0-9.]+)/i },
    { name: 'Sleipnir', regex: /sleipnir\/([0-9.]+)/i },
    { name: 'Lunascape', regex: /lunascape\/([0-9.]+)/i },
    { name: 'Avant Browser', regex: /avant\/([0-9.]+)/i },
    { name: 'Flock', regex: /flock\/([0-9.]+)/i },
    { name: 'RockMelt', regex: /rockmelt\/([0-9.]+)/i },
    { name: 'Camino', regex: /camino\/([0-9.]+)/i },
    { name: 'TenFourFox', regex: /tenfourfox\/([0-9.]+)/i },
    { name: 'Arora', regex: /arora\/([0-9.]+)/i },
    { name: 'Rekonq', regex: /rekonq\/([0-9.]+)/i },
    { name: 'QupZilla', regex: /qupzilla\/([0-9.]+)/i },
    { name: 'Falkon', regex: /falkon\/([0-9.]+)/i },
    { name: 'Min Browser', regex: /min\/([0-9.]+)/i },
    { name: 'Beaker Browser', regex: /beaker\/([0-9.]+)/i },

    // TV and Smart Device browsers
    { name: 'Smart TV WebKit', regex: /smarttv.*webkit/i },
    { name: 'HbbTV', regex: /hbbtv\/([0-9.]+)/i },
    { name: 'NetCast', regex: /netcast\.tv/i },
    { name: 'Apple TV', regex: /apple tv.*version\/([0-9.]+)/i },
    { name: 'Roku', regex: /roku.*version\/([0-9.]+)/i },
    { name: 'Fire TV', regex: /aftb.*version\/([0-9.]+)/i },
    { name: 'Android TV', regex: /android tv.*version\/([0-9.]+)/i },

    // Other niche browsers
    { name: 'WebPositive', regex: /webpositive\/([0-9.]+)/i },
    { name: 'Dillo', regex: /dillo\/([0-9.]+)/i },
    { name: 'NetSurf', regex: /netsurf\/([0-9.]+)/i },
    { name: 'Mosaic', regex: /mosaic\/([0-9.]+)/i },
    { name: 'IBrowse', regex: /ibrowse\/([0-9.]+)/i },
    { name: 'AWeb', regex: /aweb\/([0-9.]+)/i },
    { name: 'Voyager', regex: /voyager\/([0-9.]+)/i },
    { name: 'AmigaVoyager', regex: /amigavoyager\/([0-9.]+)/i },
    { name: 'Uzbl', regex: /uzbl\/([0-9.]+)/i },
    { name: 'Surf', regex: /surf\/([0-9.]+)/i },
    { name: 'Nyxt', regex: /nyxt\/([0-9.]+)/i },
  ];

  // Fixed: Replaced for...of loop with Array.find() to avoid no-restricted-syntax
  browserTests.find((test) => {
    const match = userAgent.match(test.regex);
    if (match && (!test.exclude || !userAgent.match(test.exclude))) {
      browser = test.name;
      version = match[1] || '';

      return true;
    }

    return false;
  });

  // Special handling for some browsers that need additional logic
  if (browser === 'Unknown') {
    if (ua.includes('webkit') && ua.includes('safari')) {
      browser = 'WebKit Browser';
    } else if (ua.includes('gecko') && ua.includes('rv:')) {
      browser = 'Gecko Browser';
      const match = userAgent.match(/rv:([0-9.]+)/);
      version = match ? match[1] : '';
    } else if (ua.includes('trident')) {
      browser = 'Trident Browser';
    } else if (ua.includes('presto')) {
      browser = 'Presto Browser';
    }
  }

  const os = detectOS(userAgent);
  const deviceType = detectDeviceType(userAgent);

  return {
    os,
    brand: isMobileApp ? 'Mobile App - GoNative' : version ? `${browser} V${version}` : browser,
    device: deviceType,
    method: 'comprehensive-parsing',
  };
};
